import type { DateTime } from 'luxon'

import type { GatingGroup, SessionAttendancePolicy } from '@/queries/constants'

import type { FormInputType } from '@/components/UI/Form/types'
import type { UserDetails, WorldBase } from '@/components/Worlds/types'

import type { SerializedRichText } from '../richText/types'
import type { SessionTag } from '../tags'
import type {
  RECURRING_SERIES_TEMPLATE_SCHEDULE_TYPES,
  SERIES_TEMPLATE_SCHEDULE_TYPES,
  SERIES_TEMPLATE_TYPES,
} from './constants'

export type ReocurringSeriesTemplateScheduleType =
  (typeof RECURRING_SERIES_TEMPLATE_SCHEDULE_TYPES)[number]
export type SeriesTemplateScheduleType = (typeof SERIES_TEMPLATE_SCHEDULE_TYPES)[number]
export type SeriesTemplateType = (typeof SERIES_TEMPLATE_TYPES)[number]

export enum SeriesTemplatePeerReviewVisibility {
  Public = 'public',
  Unlisted = 'unlisted',
}

export interface TemplateDetails {
  id: number
  title: string
  description: SerializedRichText
  externalDescription: SerializedRichText | null
  countSessions?: number
  scheduleType?: SeriesTemplateScheduleType
  maxLearners: number
  active: boolean
  allowCohosts: boolean
  worldId: number | null
  world: WorldBase | null
  tutorTraining: {
    id: number
    title: string
    linkTraining: string
    slug: string
  } | null
  createdAt: DateTime
}

export interface SeriesTemplateDetails extends TemplateDetails {
  id: number
  slug: string | null
  title: string
  externalDescription: SerializedRichText
  description: SerializedRichText
  curriculum: SerializedRichText | null
  curriculumUrl: string | null
  attendancePolicy: string
  scheduleType: SeriesTemplateScheduleType
  questionnaire: FormInputType[] | null
  countSessions: number
  maxLearners: number
  active: boolean
  enabled: boolean
  allowCohosts: boolean
  user: Pick<UserDetails, 'id' | 'displayName' | 'username'>
  course: {
    id: number
    name: string
    slug: string
  } | null
  worldId: number | null
  world: WorldBase | null
  assessmentIds: number[]
  gatingGroup: GatingGroup | null
  minimumStartDate: DateTime | null
  type: SeriesTemplateType
  maxRegistrationsPerLearner: number | null
  maxActiveSeriesPerTutor: number | null
  peerReviewTags: SessionTag[] | null
  peerReviewVisibility: SeriesTemplatePeerReviewVisibility
  createdAt: DateTime
  sessionTemplateDetails: SessionTemplateDetails[]
}

export interface SessionTemplateDetails extends TemplateDetails {
  id: number
  title: string
  description: SerializedRichText
  externalDescription: SerializedRichText | null
  startDate: DateTime | null
  durationMinutes: number
  curriculum: SerializedRichText | null
  questionnaire: FormInputType[] | null
  maxLearners: number
  active: boolean
  seriesPosition: number | null
  allowCohosts: boolean
  user: Pick<UserDetails, 'id' | 'displayName' | 'username'>
  topic: { id: number; name: string } | null
  worldId: number | null
  world: WorldBase | null
  attendancePolicy: SessionAttendancePolicy
  isBreakoutEnabled: boolean
  createdAt: DateTime
  seriesTemplate?: SeriesTemplateDetails | null
}

export type TutorEligibility = {
  canUserTutor: boolean
  trainingRequirementMet: boolean
  topicRequirementMet: boolean
  hostingMaxActiveSeries: boolean
}
